import api from './api';
import { filterProjectCollections } from '../config/collections.config';

export const simulationService = {
  async getProjects() {
    try {
      const response = await api.get('/api/simulation/projects');
      return {
        success: true,
        data: response.data.data || [], // Project names for backward compatibility
        projects: response.data.projects || [] // Full project data
      };
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  },

  async getProjectData(projectId) {
    try {
      const response = await api.get(`/api/simulation/projects/${projectId}`);
      const data = response.data;

      // Extract earliest and latest timestamps
      const timestamps = data.deliveryNotes
        .map(note => note.standard?.documentInfo?.timeStamp)
        .filter(Boolean)
        .map(t => new Date(t).getTime())
        .filter(t => !isNaN(t));

      return {
        success: true,
        timeframe: timestamps.length > 0 ? {
          start: Math.min(...timestamps),
          end: Math.max(...timestamps)
        } : null,
        data: data.deliveryNotes,
        location: data.location || null
      };
    } catch (error) {
      console.error('Error fetching project data:', error);
      throw error;
    }
  },

  async calculateCO2e(calculationData) {
    try {
      const response = await api.post('/api/simulation/calculate-co2e', calculationData);
      return response.data;
    } catch (error) {
      console.error('Error calculating CO2e:', error);
      throw error;
    }
  },

  async getEmissionFactors() {
    try {
      const response = await api.get('/api/simulation/emission-factors');
      return response.data;
    } catch (error) {
      console.error('Error fetching emission factors:', error);
      throw error;
    }
  },

  async saveCalculation(projectId, calculationData) {
    try {
      const response = await api.post(`/api/simulation/projects/${projectId}/calculations`, calculationData);
      return response.data;
    } catch (error) {
      console.error('Error saving calculation:', error);
      throw error;
    }
  },

  async getCalculationHistory(projectId) {
    try {
      const response = await api.get(`/api/simulation/projects/${projectId}/calculations`);
      return response.data;
    } catch (error) {
      console.error('Error fetching calculation history:', error);
      throw error;
    }
  },

  async updateCO2Values(projectId, data) {
    try {
      // Validate required fields
      if (!data.material || !data.location || !data.location.postalCode || !data.location.city) {
        throw new Error('Missing required fields: material and location details are required');
      }

      // Ensure co2Value and breakdown are present
      if (data.co2Value === undefined || !data.breakdown) {
        throw new Error('Missing required fields: co2Value and breakdown are required');
      }

      // Format request payload
      const payload = {
        material: data.material,
        location: {
          postalCode: data.location.postalCode,
          city: data.location.city
        },
        co2Value: data.co2Value,
        breakdown: {
          A1: data.breakdown.A1 || 0,
          A2: data.breakdown.A2 || 0,
          A3: data.breakdown.A3 || 0,
          A4: data.breakdown.A4 || 0
        }
      };

      // Add calculation inputs if provided
      if (data.calculationInputs) {
        payload.calculationInputs = data.calculationInputs;
      }

      const response = await api.post(`/api/simulation/projects/${projectId}/co2-values`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating CO2 values:', error);
      throw error;
    }
  },

  async getLastCalculation(projectId, material, location) {
    try {
      if (!projectId || !material || !location || !location.postalCode || !location.city) {
        throw new Error('Missing required parameters: projectId, material, and location details are required');
      }

      const response = await api.get('/api/simulation/last-calculation', {
        params: {
          projectId,
          material,
          postalCode: location.postalCode,
          city: location.city
        }
      });

      return response.data;
    } catch (error) {
      // Return a standardized response for 404 errors
      if (error.response && error.response.status === 404) {
        return {
          success: false,
          data: null,
          message: 'No saved calculation found for this material and location'
        };
      }

      // Log other errors but don't throw them to prevent cascading failures
      console.error('Error fetching last calculation:', error);
      return {
        success: false,
        data: null,
        error: error.message || 'Unknown error'
      };
    }
  },

  async createProject(projectData) {
    try {
      if (!projectData.projectName || !projectData.location || !projectData.location.lat || !projectData.location.lng) {
        throw new Error('Project name and location coordinates are required');
      }

      const response = await api.post('/api/simulation/projects', {
        projectName: projectData.projectName.trim(),
        location: {
          lat: projectData.location.lat,
          lng: projectData.location.lng,
          address: projectData.location.address
        },
        startDate: projectData.startDate || null,
        endDate: projectData.endDate || null
      });

      return response.data;
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  },

  async deleteProject(projectId) {
    try {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      const response = await api.delete(`/api/simulation/projects/${projectId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  },

  async renameProject(projectId, newProjectName) {
    try {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      if (!newProjectName) {
        throw new Error('New project name is required');
      }

      const response = await api.put(`/api/simulation/projects/${projectId}/rename`, { newProjectName });
      return response.data;
    } catch (error) {
      console.error('Error renaming project:', error);
      throw error;
    }
  },

  async getProjectMetadata(projectId) {
    try {
      if (!projectId) {
        throw new Error('Project ID is required');
      }
      const response = await api.get(`/api/simulation/projects/${encodeURIComponent(projectId)}/metadata`);
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return { success: false, data: null, error: 'No metadata found' };
      }
      console.error('Error fetching project metadata:', error);
      throw error;
    }
  },

  async updateProject(projectId, updates) {
    try {
      if (!projectId) {
        throw new Error('Project ID is required');
      }
      const response = await api.put(`/api/simulation/projects/${encodeURIComponent(projectId)}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating project:', error);
      // Surface backend error messages so the UI can display them
      if (error.response && error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  },

  async uploadMaterialSpecification(file, projectName = 'Simulation Project') {
    try {
      if (!file) {
        throw new Error('File is required');
      }

      // Validate file type
      if (file.type !== 'application/pdf') {
        throw new Error('Only PDF files are allowed');
      }

      // Validate file size (16MB limit)
      const maxSize = 16 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('File size must be less than 16MB');
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('project_name', projectName);

      // Use environment variable for PDF processor URL
      const PDF_PROCESSOR_URL = process.env.REACT_APP_PDF_PROCESSOR_URL || 'http://localhost:8000';
      console.log('Using PDF_PROCESSOR_URL:', PDF_PROCESSOR_URL);
      const response = await fetch(`${PDF_PROCESSOR_URL}/api/simulation/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Upload failed with status ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error uploading material specification:', error);
      throw error;
    }
  },

  // Construction Layer Areas methods
  async saveLayerAreas(projectId, layerAreasData) {
    try {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      if (!layerAreasData || typeof layerAreasData !== 'object') {
        throw new Error('Layer areas data is required');
      }

      const { deckschichten, binderschichten, tragschichten, quantitySource } = layerAreasData;

      const response = await api.post(`/api/simulation/projects/${projectId}/layer-areas`, {
        deckschichten: deckschichten || [],
        binderschichten: binderschichten || [],
        tragschichten: tragschichten || [],
        quantitySource: quantitySource || 'deliveryNotes'
      });

      return response.data;
    } catch (error) {
      console.error('Error saving layer areas:', error);
      throw error;
    }
  },

  async getLayerAreas(projectId) {
    try {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      const response = await api.get(`/api/simulation/projects/${projectId}/layer-areas`);
      return response.data;
    } catch (error) {
      // Return a standardized response for 404 errors (no layer areas found)
      if (error.response && error.response.status === 404) {
        return {
          success: false,
          data: null,
          message: 'No layer areas found for this project'
        };
      }

      console.error('Error fetching layer areas:', error);
      throw error;
    }
  },

  // Update dichte (t/m³) for every layer entry in this project whose material
  // matches `materialName`. Numerically equivalent to rohdichte (g/cm³).
  // Backend recalculates `menge` on save, so mass totals refresh automatically.
  async updateMaterialDichte(projectId, materialName, dichte) {
    if (!projectId) throw new Error('Project ID is required');
    if (!materialName) throw new Error('Material name is required');
    if (typeof dichte !== 'number' || !isFinite(dichte) || dichte <= 0) {
      throw new Error('Valid dichte value is required');
    }

    const current = await this.getLayerAreas(projectId);
    if (!current?.success || !current.data) {
      return { success: false, updatedCount: 0, reason: 'no-layer-areas' };
    }

    const data = current.data;
    let updatedCount = 0;

    const applyToEntries = (entries) =>
      (entries || []).map(e => {
        if ((e.material || '').trim() === materialName.trim()) {
          updatedCount += 1;
          return { ...e, dichte };
        }
        return e;
      });

    const payload = {
      deckschichten: applyToEntries(data.deckschichten),
      binderschichten: applyToEntries(data.binderschichten),
      tragschichten: applyToEntries(data.tragschichten),
      quantitySource: data.quantitySource || 'deliveryNotes'
    };

    if (updatedCount === 0) {
      return { success: false, updatedCount: 0, reason: 'no-material-match' };
    }

    const saved = await this.saveLayerAreas(projectId, payload);
    return { success: !!saved?.success, updatedCount, data: saved?.data };
  },

  // Quantity source methods
  async getQuantitySource(projectId) {
    try {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      const response = await api.get(`/api/simulation/projects/${projectId}/quantity-source`);
      return {
        ...response.data,
        data: {
          ...response.data.data,
          hasLayerAreas: true
        }
      };
    } catch (error) {
      // If 404, it means no layer areas exist yet - return default
      if (error.response && error.response.status === 404) {
        return {
          success: true,
          data: {
            quantitySource: 'deliveryNotes',
            hasLayerAreas: false
          }
        };
      }
      console.error('Error fetching quantity source:', error);
      throw error;
    }
  },

  async updateQuantitySource(projectId, quantitySource) {
    try {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      if (!quantitySource || !['deliveryNotes', 'calculatedLayers'].includes(quantitySource)) {
        throw new Error('Valid quantity source is required (deliveryNotes or calculatedLayers)');
      }

      const response = await api.put(`/api/simulation/projects/${projectId}/quantity-source`, {
        quantitySource
      });

      return response.data;
    } catch (error) {
      console.error('Error updating quantity source:', error);
      throw error;
    }
  },

  // Layer CO2 methods — now take entryIndex to address a specific array entry
  async saveLayerCO2(projectId, layerType, entryIndex, co2Data) {
    try {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      if (!co2Data || !co2Data.co2Value || !co2Data.co2Breakdown) {
        throw new Error('CO2 value and breakdown are required');
      }

      const response = await api.put(
        `/api/simulation/projects/${projectId}/layer-areas/co2/${layerType}/${entryIndex}`,
        {
          co2Value: co2Data.co2Value,
          co2Breakdown: co2Data.co2Breakdown,
          calculationInputs: co2Data.calculationInputs || {}
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error saving layer CO2:', error);
      throw error;
    }
  },

  async getLayerCO2(projectId, layerType, entryIndex = 0) {
    try {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      const response = await api.get(
        `/api/simulation/projects/${projectId}/layer-areas/co2/${layerType}/${entryIndex}`
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return { success: false, data: null, message: 'No CO2 data found for this layer' };
      }
      console.error('Error fetching layer CO2:', error);
      return { success: false, data: null, error: error.message || 'Unknown error' };
    }
  }
};